import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Event as EventIcon,
  StickyNote2Outlined as MemoIcon,
} from '@mui/icons-material';
import {
  DraftBanner,
  DraftSavedHint,
  SectionLabel,
  stickyActionsSx,
  stickyTitleSx,
} from './formParts';
import {
  RECORD_KIND_META,
  RecordCarry,
  RecordKind,
  isScheduleKind,
} from './RecordKindSelector';
import RichTextEditor from '../editor/RichTextEditor';
import { useEditorImageUpload } from '../editor/useEditorImageUpload';
import useUserDirectory from '../../hooks/useUserDirectory';
import useFormDraft from '../../hooks/useFormDraft';
import { SiteScheduleService } from '../../services/SiteScheduleService';
import { getUserInfo } from '../../utils/auth';
import { Project } from '../../models/Project';
import {
  SiteSchedule,
  ScheduleEventType,
  ScheduleStatus,
  SCHEDULE_EVENT_TYPES,
  EVENT_TYPE_LABEL,
  MEMO_EVENT_TYPE,
  CreateSiteScheduleRequest,
  UpdateSiteScheduleRequest,
} from '../../models/SiteSchedule';

/**
 * 일정 / 메모 등록·수정 **단일 공용** 다이얼로그.
 *
 * 모든 등록 진입점(현장 목록 페이지, 현장 상세 일정 섹션·리스트, 다가오는 일정
 * 위젯, 캘린더)이 이 다이얼로그 하나만 쓴다 — 진입 지점마다 폼이 다르면
 * "어디서 등록했느냐" 에 따라 쓸 수 있는 필드가 달라진다.
 *
 * ## 일정과 메모
 *
 * 최상단 토글로 **일정 / 메모** 를 고른다. 둘은 같은 테이블(event_type)이지만
 * 다루는 방식이 다르다:
 *
 *  - **일정**: 날짜가 필수다. 캘린더에 뜬다. 유형(출장/점검/…)을 갖는다.
 *  - **메모**: 날짜가 **선택**이다. 날짜를 비우면 캘린더에 뜨지 않고 목록에만
 *    남는다. "언제인지는 모르지만 남겨둬야 하는" 내용을 위한 것 — 날짜를 억지로
 *    채우게 만드는 게 기존 불편의 핵심이었다.
 *
 * ## 초안 자동 저장
 *
 * 입력 내용은 브라우저에 자동 저장된다(useFormDraft). 실수로 닫거나 탭이 죽어도
 * 다시 열면 상단 배너로 이어쓰기를 제안한다. **자동으로 채우지는 않는다** —
 * 새로 쓰려고 열었는데 옛 내용이 들어차 있으면 그게 더 혼란스럽다.
 *
 * ## 모바일
 *
 * sm 이하에서는 전체 화면으로 열리고, 모든 필드가 한 열로 쌓인다. 유형은
 * select 대신 칩으로 노출해 한 번의 탭으로 고르게 했다(select 는 열고-고르고-닫는
 * 3동작). 하단 액션바는 고정되어 스크롤 위치와 무관하게 저장에 닿을 수 있다.
 */

/**
 * 저장 실패 메시지. 403 은 그냥 "권한 없음" 으로 끝내면 안 된다 — 권한을 방금
 * 받은 경우가 실제로 많고, 그때도 똑같이 막히기 때문이다: role 은 로그인할 때
 * 발급한 JWT 안에 들어 있고(백엔드 RequireRole 은 토큰의 role 을 본다) access
 * token 수명이 30일이라, 관리자가 등급을 올려도 재로그인 전까지는 옛 권한으로
 * 계속 거절된다. axios 기본 문구("Request failed with status code 403")로는
 * 그걸 알 길이 없어 "고쳤는데 왜 안 되지" 가 된다.
 */
export function saveErrorMessage(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 403) {
    return '권한이 없습니다 — 일정·메모 등록은 admin·editor 만 할 수 있습니다. ' +
      '방금 권한을 받았다면 로그아웃 후 다시 로그인하세요 (권한은 로그인할 때 발급된 토큰에 들어 있습니다).';
  }
  return e instanceof Error ? e.message : '저장 실패';
}

/** admin/editor 만 일정·메모 생성·수정 가능. */
export function canEditSchedule(): boolean {
  const u = getUserInfo();
  return u?.role === 'admin' || u?.role === 'editor';
}

/**
 * 이 폼이 다루는 두 종류. RecordKind 중 site_schedules 로 저장되는 갈래이며
 * event_type 에서 파생되는 표시용 개념이다 (memo 인지 아닌지).
 */
type FormMode = 'schedule' | 'memo';

/** 일정 모드에서 고를 수 있는 유형 — 메모는 토글로 분리했으므로 제외한다. */
const SCHEDULE_ONLY_EVENT_TYPES = SCHEDULE_EVENT_TYPES.filter((t) => t !== MEMO_EVENT_TYPE);

/** 일정 모드로 돌아올 때의 기본 유형. */
const DEFAULT_SCHEDULE_EVENT_TYPE: ScheduleEventType = 'visit';

/**
 * 상태는 폼에서 고르지 않는다 — 실제로 아무도 갱신하지 않아 전부 '예정' 으로
 * 남았고, 그런 값은 화면에서 뜻을 갖지 못한다. 컬럼은 NOT NULL 이고 기존 행에
 * 값이 들어 있으므로, 신규 생성 시에만 이 값을 넣고 수정 시에는 아예 보내지
 * 않는다(미변경 → 기존 값 보존).
 */
const DEFAULT_STATUS: ScheduleStatus = 'planned';

interface FormState {
  siteId: string; // selectableSites 모드에서만 사용.
  event_type: ScheduleEventType;
  title: string;
  description: string;
  scheduled_at: string; // datetime-local
  scheduled_end_at: string; // datetime-local (optional)
  assignees: string[]; // 다중 담당자 — 저장 시 ", " 로 합쳐 단일 컬럼에 기록.
}

const EMPTY: FormState = {
  siteId: '',
  event_type: DEFAULT_SCHEDULE_EVENT_TYPE,
  title: '',
  description: '',
  scheduled_at: '',
  scheduled_end_at: '',
  assignees: [],
};

const modeOf = (t: ScheduleEventType): FormMode => (t === MEMO_EVENT_TYPE ? 'memo' : 'schedule');

const toIso = (local: string): string => {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** "a, b" → ["a","b"]. 빈/공백 토큰 제거. */
export const splitAssignees = (csv?: string | null): string[] =>
  (csv ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const fromSchedule = (s: SiteSchedule): FormState => ({
  siteId: String(s.site_project_id),
  event_type: s.event_type,
  title: s.title,
  description: s.description ?? '',
  scheduled_at: toLocalInput(s.scheduled_at),
  scheduled_end_at: toLocalInput(s.scheduled_end_at),
  assignees: splitAssignees(s.assignee),
});

/** 사용자가 실제로 뭔가 쓴 폼인가 — 빈 폼은 초안으로 저장하지 않는다. */
const isBlankForm = (f: FormState): boolean =>
  !f.title.trim() && !f.description.trim();

const toCreateRequest = (form: FormState): CreateSiteScheduleRequest => {
  const startIso = form.scheduled_at ? toIso(form.scheduled_at) : '';
  return {
    event_type: form.event_type,
    status: DEFAULT_STATUS,
    title: form.title.trim(),
    description: form.description || undefined,
    // 메모는 날짜 없이 저장할 수 있다 — 백엔드가 null 을 받아 그대로 비워 둔다.
    scheduled_at: startIso || null,
    scheduled_end_at: startIso && form.scheduled_end_at ? toIso(form.scheduled_end_at) : null,
    assignee: form.assignees.map((a) => a.trim()).filter(Boolean).join(', ') || undefined,
  };
};

const toUpdateRequest = (form: FormState): UpdateSiteScheduleRequest => {
  const startIso = form.scheduled_at ? toIso(form.scheduled_at) : '';
  const endIso = startIso && form.scheduled_end_at ? toIso(form.scheduled_end_at) : '';
  return {
    event_type: form.event_type,
    title: form.title.trim(),
    description: form.description,
    // 날짜를 **비우는** 것은 값 생략과 구분되지 않으므로(JSON null → Go nil)
    // clear_* 플래그로 명시해야 한다. 일정 → 메모 전환 경로.
    ...(startIso ? { scheduled_at: startIso } : { clear_scheduled_at: true }),
    ...(endIso ? { scheduled_end_at: endIso } : { clear_scheduled_end_at: true }),
    assignee: form.assignees.map((a) => a.trim()).filter(Boolean).join(', '),
  };
};

export interface ScheduleFormDialogProps {
  open: boolean;
  /** null = 신규 생성, SiteSchedule = 수정. */
  editing: SiteSchedule | null;
  onClose: () => void;
  /** 저장 성공 후 호출 — 부모가 닫기/새로고침/스낵을 처리. */
  onSaved: (saved: SiteSchedule, mode: 'create' | 'edit') => void;
  /** 단일 현장 컨텍스트. selectableSites 와 둘 중 하나만. 수정 모드는 editing.id 사용. */
  projectId?: number;
  /**
   * 단일 현장 컨텍스트일 때 표시할 현장 이름. 넘기면 현장 필드를 보여주되
   * 현재 현장으로 고정한다. (현장 상세 페이지 진입점)
   */
  siteName?: string;
  /** 현장 선택 모드(현장 목록 페이지 등 현장 무관 진입점). 비어 있지 않아야 한다. */
  selectableSites?: Project[];
  /** 신규 생성 시 시작 일시 프리셋. 안정적 참조(state)로 전달 권장. */
  presetStart?: Date | null;
  /**
   * 신규 생성 시 종료 일시 프리셋 — 캘린더에서 기간을 드래그해 들어온 경로.
   * presetStart 와 함께 넘어올 때만 적용된다 (시작 없는 종료는 성립하지 않음).
   */
  presetEnd?: Date | null;
  /**
   * 신규 생성 시 처음 선택될 유형. 'memo' 를 주면 메모 모드로 열린다 —
   * "메모 등록" 버튼이 일정 폼을 거치지 않고 바로 메모를 쓰게 하는 경로.
   */
  initialEventType?: ScheduleEventType;
  /**
   * 최상단 종류 선택기에 노출할 선택지. 기본은 이 폼이 직접 다룰 수 있는
   * 일정/메모 둘. 통합 등록 진입점(RecordFormDialog)은 파일·출장 일지까지 넘겨
   * 하나의 선택기에서 전부 고르게 한다.
   */
  availableKinds?: RecordKind[];
  /**
   * 이 폼이 처리할 수 없는 종류(파일·출장 일지)가 선택됐을 때 부모에게 넘긴다.
   * 지금까지 쓴 공통 입력값을 carry 로 함께 넘겨 다시 타이핑하지 않게 한다.
   * 넘기지 않으면 해당 선택지는 애초에 노출되지 않는다.
   */
  onKindChange?: (kind: RecordKind, carry: RecordCarry) => void;
  /** 다른 종류에서 옮겨올 때 이어받는 공통 입력값. */
  initialCarry?: RecordCarry | null;
}

const ScheduleFormDialog: React.FC<ScheduleFormDialogProps> = ({
  open,
  editing,
  onClose,
  onSaved,
  projectId,
  siteName,
  selectableSites,
  presetStart,
  presetEnd,
  initialEventType,
  availableKinds,
  onKindChange,
  initialCarry,
}) => {
  const theme = useTheme();
  // 다크모드에서 datetime-local 의 네이티브 달력 아이콘이 검정으로 그려져 안
  // 보이는 문제 — colorScheme 을 명시해 브라우저가 배경에 맞게 그리게 한다.
  const isDark = theme.palette.mode === 'dark';
  // 모바일에서는 전체 화면. 좁은 화면의 모달 다이얼로그는 좌우 여백만 먹고
  // 실제 입력 영역이 좁아진다.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { names: userNames, users } = useUserDirectory();
  const mentionUsers = useMemo(() => users.map((u) => ({ id: u.id, name: u.name })), [users]);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 일정 → 메모 → 일정 으로 돌아올 때 원래 유형을 되돌려 준다. 유형을 고르고
  // 메모를 한 번 눌러 본 사용자가 처음부터 다시 고르게 만들지 않는다.
  const lastScheduleTypeRef = useRef<ScheduleEventType>(DEFAULT_SCHEDULE_EVENT_TYPE);

  /**
   * 지금 날짜칸의 값이 **사용자 의도가 아니라** 폼이 대신 채워 넣은 것인가.
   *
   * 신규 일정 폼은 편의상 현재 시각을 미리 채운다. 그 상태에서 메모로 바꾸면,
   * 사용자는 날짜를 만진 적이 없는데도 메모가 "지금" 날짜를 달고 캘린더에
   * 올라간다 — 날짜 없는 메모를 쓰려던 의도와 정반대다. 그래서 자동으로 채운
   * 값은 메모 전환 시 버린다. 사용자가 직접 고친 날짜나 캘린더 날짜 클릭으로
   * 들어온 presetStart 는 실제 의도이므로 유지한다.
   */
  const dateAutoSeededRef = useRef(false);

  // selectableSites 가 주어지면 수정 모드에서도 현장을 바꿀 수 있게 한다 —
  // 단일 현장 컨텍스트(projectId+siteName)에서는 여전히 고정 표시.
  const isSelectMode = selectableSites != null && selectableSites.length > 0;
  const mode = modeOf(form.event_type);
  const isMemo = mode === 'memo';

  const initialSiteId = useMemo(() => {
    if (projectId != null) return String(projectId);
    if (selectableSites && selectableSites.length === 1) return String(selectableSites[0].id);
    return '';
  }, [projectId, selectableSites]);

  // 인라인 이미지 업로드 대상 현장 — 선택 모드 미선택이면 비활성.
  const imageSiteId = projectId != null ? projectId : Number(form.siteId);
  const canUploadImage = Number.isFinite(imageSiteId) && imageSiteId > 0;
  const uploadImage = useEditorImageUpload(canUploadImage ? imageSiteId : 0);

  /**
   * 초안 키 — 수정은 대상 id 로, 신규는 현장으로 나눈다. 한 키를 공유하면
   * A 현장에 쓰던 초안이 B 현장 등록 폼에 나타난다.
   */
  const draftKey = editing
    ? `schedule:edit:${editing.id}`
    : `schedule:new:${projectId ?? 'pick'}`;
  const { recovered, savedAt, dismissRecovered, clear: clearDraftState } = useFormDraft(form, {
    key: draftKey,
    enabled: open,
    isEmpty: isBlankForm,
  });

  /**
   * 프리셋 날짜는 호출 측에서 `new Date(...)` 로 만들어 넘기는 곳이 많다 —
   * 객체 그대로를 아래 초기화 effect 의 deps 에 두면 부모가 리렌더될 때마다
   * 새 identity 가 되어 **작성 중인 폼이 통째로 초기화된다**. 값(ms)으로 비교한다.
   */
  const presetStartMs = presetStart ? presetStart.getTime() : null;
  const presetEndMs = presetEnd ? presetEnd.getTime() : null;

  // 열릴 때마다 editing(수정) 또는 preset(신규)으로 초기화.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm(fromSchedule(editing));
      dateAutoSeededRef.current = false; // 저장된 날짜는 실제 데이터다
      if (editing.event_type !== MEMO_EVENT_TYPE) {
        lastScheduleTypeRef.current = editing.event_type;
      }
    } else {
      const startType = initialEventType ?? DEFAULT_SCHEDULE_EVENT_TYPE;
      if (startType !== MEMO_EVENT_TYPE) lastScheduleTypeRef.current = startType;
      const isMemoStart = startType === MEMO_EVENT_TYPE;
      const presetLocal = presetStart ? toLocalInput(presetStart.toISOString()) : '';
      // 다른 종류에서 옮겨온 날짜 중 **폼이 자동으로 채운 값** 은 메모로 올 때
      // 버린다. 그러지 않으면 날짜 없는 메모를 쓰려던 의도와 반대로 "지금" 이
      // 따라붙어 캘린더에 올라간다.
      const carriedLocal =
        initialCarry && !(isMemoStart && initialCarry.datetimeAuto)
          ? initialCarry.datetimeLocal
          : '';
      // 메모는 날짜가 선택이므로 비워 두고, 일정은 지금 시각부터 채워 준다.
      const seeded =
        carriedLocal || presetLocal || (isMemoStart ? '' : toLocalInput(new Date().toISOString()));
      // 옮겨온 값도 캘린더 날짜 클릭(presetStart)도 아니면 폼이 대신 넣은 값이다.
      dateAutoSeededRef.current = !!seeded && !carriedLocal && !presetLocal;
      // 종료 프리셋은 시작이 실제로 프리셋 값으로 채워졌을 때만 따라간다 —
      // 시작이 다른 경로(carry 등)로 정해졌는데 종료만 드래그 범위면 어긋난다.
      const seededEnd =
        presetLocal && seeded === presetLocal && presetEnd
          ? toLocalInput(presetEnd.toISOString())
          : '';
      // 신규 등록 시 작성자를 기본 담당자로 넣어 준다 — chip 삭제로 제거 가능.
      const me = getUserInfo()?.name;
      const carriedPeople = (initialCarry?.people ?? []).filter(Boolean);
      setForm({
        ...EMPTY,
        event_type: startType,
        siteId: initialCarry?.siteId || initialSiteId,
        // 필드 이름은 종류마다 달라도 담는 뜻은 같다 — 목적→제목, 작업 내용→내용.
        title: initialCarry?.title ?? '',
        description: initialCarry?.body ?? '',
        scheduled_at: seeded,
        scheduled_end_at: seededEnd,
        assignees: carriedPeople.length > 0 ? carriedPeople : me ? [me] : [],
      });
    }
    setFormError(null);
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presetStart/End 는 값(ms)으로 비교한다
  }, [open, editing, presetStartMs, presetEndMs, initialSiteId, initialEventType, initialCarry]);

  /** 초안 이어쓰기 — 저장된 값을 폼에 적용하고 배너를 닫는다. */
  const applyRecovered = useCallback(() => {
    if (!recovered) return;
    setForm(recovered.value);
    dismissRecovered();
  }, [recovered, dismissRecovered]);

  const switchMode = (next: FormMode) => {
    setForm((f) => {
      if (next === 'memo') {
        if (f.event_type !== MEMO_EVENT_TYPE) lastScheduleTypeRef.current = f.event_type;
        // 사용자가 직접 넣은 날짜는 유지한다 — 날짜 있는 메모는 유효하고,
        // 되돌릴 때 다시 입력하게 만들 이유도 없다. 폼이 자동으로 채운 값만 버린다.
        if (dateAutoSeededRef.current) {
          return { ...f, event_type: MEMO_EVENT_TYPE, scheduled_at: '', scheduled_end_at: '' };
        }
        return { ...f, event_type: MEMO_EVENT_TYPE };
      }
      // 일정은 날짜가 필수다. 비어 있으면 지금 시각으로 채워 준다 — 빈칸을 두고
      // 검증 실패를 보여주는 것보다 낫다. 다시 채운 값도 자동 채움으로 표시해,
      // 메모로 되돌아갈 때 또 따라붙지 않게 한다.
      if (!f.scheduled_at) dateAutoSeededRef.current = true;
      return {
        ...f,
        event_type: lastScheduleTypeRef.current,
        scheduled_at: f.scheduled_at || toLocalInput(new Date().toISOString()),
      };
    });
    setFormError(null);
  };

  /** 종류를 옮길 때 함께 넘길 공통 입력값. */
  const carryOf = (f: FormState): RecordCarry => ({
    siteId: f.siteId,
    title: f.title,
    body: f.description,
    people: f.assignees,
    datetimeLocal: f.scheduled_at,
    datetimeAuto: dateAutoSeededRef.current,
  });

  /**
   * 종류 선택기 변경. 일정↔메모는 같은 테이블이라 이 폼 안에서 처리해 입력값을
   * 그대로 유지하고, 폼 자체가 달라지는 종류(파일·출장 일지)만 부모에게 넘긴다.
   */
  const handleKindChange = (next: RecordKind) => {
    if (isScheduleKind(next)) {
      switchMode(next as FormMode);
      return;
    }
    onKindChange?.(next, carryOf(form));
  };

  /**
   * 노출할 선택지. 부모가 전환을 받아줄 수 없거나 수정 모드면 이 폼이 실제로
   * 처리할 수 있는 둘만 남긴다 — 누를 수 없는 선택지는 화면만 넓힌다. 수정은
   * 특히 변환이 불가능하다(엔티티가 다르면 옮길 데이터가 없다).
   */
  const kinds = useMemo<RecordKind[]>(() => {
    const wanted = availableKinds ?? ['schedule', 'memo'];
    return onKindChange && !editing ? wanted : wanted.filter(isScheduleKind);
  }, [availableKinds, onKindChange, editing]);

  const validate = (): string | null => {
    // 메모는 현장을 비워 〈공통〉으로 남길 수 있다. 일정은 어느 현장 일인지가
    // 곧 내용이라 현장 없는 출장·점검은 만들어 두면 아무도 처리하지 못한다
    // (백엔드도 같은 규칙으로 막는다).
    if (isSelectMode && !isMemo && (!form.siteId || Number(form.siteId) <= 0)) return '현장을 선택하세요.';
    if (!form.title.trim()) return '제목을 입력하세요.';
    // 메모만 날짜를 생략할 수 있다 (백엔드 검증과 같은 규칙).
    if (!form.scheduled_at) {
      if (!isMemo) return '일정은 시작일이 필요합니다. 날짜를 모르면 공지사항으로 남기세요.';
    } else {
      const startIso = toIso(form.scheduled_at);
      if (!startIso) return '시작일 형식이 올바르지 않습니다.';
      if (form.scheduled_end_at) {
        const endIso = toIso(form.scheduled_end_at);
        if (!endIso) return '종료일 형식이 올바르지 않습니다.';
        if (new Date(endIso) < new Date(startIso)) return '종료일은 시작일 이후여야 합니다.';
      }
    }
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setFormError(v);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        // isSelectMode 일 때만 현장 변경을 반영 — 단일 현장 컨텍스트는 siteId 가
        // 비어 있으므로(현장 필드 자체가 없음) 기존 현장 그대로 유지.
        // 현장을 **비우는** 것과 **안 건드리는** 것은 값만으로 구분되지 않는다.
        // 선택 모드에서 메모의 현장을 비웠으면 공통으로 되돌리라는 뜻이다.
        const clearSite = isSelectMode && isMemo && !form.siteId;
        const updateData: UpdateSiteScheduleRequest = {
          ...toUpdateRequest(form),
          site_project_id: isSelectMode && form.siteId ? Number(form.siteId) : undefined,
          ...(clearSite ? { clear_site_project_id: true } : {}),
        };
        const saved = await SiteScheduleService.update(editing.id, updateData);
        clearDraftState();
        onSaved(saved, 'edit');
      } else {
        // 선택 모드에서 현장을 비운 메모 → null (공통 메모 경로로 간다).
        const targetSiteId =
          projectId != null ? projectId : form.siteId ? Number(form.siteId) : null;
        const saved = await SiteScheduleService.create(targetSiteId, toCreateRequest(form));
        clearDraftState();
        onSaved(saved, 'create');
      }
    } catch (e) {
      // 저장이 실패했으면 초안은 절대 지우지 않는다 — 지금이 초안이 가장 필요한 순간이다.
      setFormError(saveErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const selectedSite = useMemo(
    () => selectableSites?.find((s) => String(s.id) === form.siteId) ?? null,
    [selectableSites, form.siteId],
  );

  const noun = isMemo ? '공지사항' : '일정';

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={stickyTitleSx}>
        {isMemo ? <MemoIcon color="action" /> : <EventIcon color="action" />}
        <Box component="span" sx={{ flex: 1, fontSize: '1.05rem', fontWeight: 700 }}>
          {editing ? `${noun} 수정` : `${isMemo ? '공지사항' : '업무'} 등록`}
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label="닫기" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
        <Stack spacing={2}>
          {/* 초안 복구 — 자동 적용하지 않고 사용자가 고르게 한다. */}
          {recovered && (
            <DraftBanner
              savedAt={recovered.savedAt}
              onResume={applyRecovered}
              onDiscard={clearDraftState}
            />
          )}

          {/* 1. 종류 — "무엇을 남기나요" 큰 선택기는 없앴다. 신규 등록은 버튼이
              곧 종류라 고를 게 없고(kinds 하나), 수정에서만 일정↔메모 전환용
              작은 토글을 남긴다 (같은 테이블이라 서로 바꿀 수 있는 유일한 짝). */}
          {kinds.length > 1 && (
            <Box>
              <SectionLabel>종류</SectionLabel>
              <Stack direction="row" role="radiogroup" aria-label="종류" sx={{ gap: 0.75 }}>
                {kinds.map((k) => (
                  <Chip
                    key={k}
                    role="radio"
                    aria-checked={mode === k}
                    label={RECORD_KIND_META[k].label}
                    onClick={() => handleKindChange(k)}
                    disabled={saving}
                    color={mode === k ? 'primary' : 'default'}
                    variant={mode === k ? 'filled' : 'outlined'}
                    sx={{ height: 34, px: 0.5, fontWeight: mode === k ? 700 : 400 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* 2. 현장 */}
          {isSelectMode ? (
            <Box>
              <SectionLabel required={!isMemo}>현장</SectionLabel>
              <Autocomplete
                options={selectableSites!}
                value={selectedSite}
                onChange={(_, v) => setForm((f) => ({ ...f, siteId: v ? String(v.id) : '' }))}
                getOptionLabel={(s) => s.name_ko || s.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={isMemo ? '비우면 공통 — 현장 이름으로 검색' : '현장 이름으로 검색'}
                    // 현장이 많아 select 스크롤로는 못 찾겠다는 게 기존 불만.
                    helperText={
                      isMemo
                        ? '비워 두면 〈공통〉으로 등록되어 전체 공지사항에만 표시됩니다'
                        : editing
                          ? `${noun}을 옮길 현장`
                          : `${noun}을 등록할 현장`
                    }
                  />
                )}
              />
            </Box>
          ) : (
            siteName && (
              <Box>
                <SectionLabel>현장</SectionLabel>
                <Chip label={siteName} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  현재 현장으로 등록됩니다
                </Typography>
              </Box>
            )
          )}

          {/* 3. 제목 */}
          <Box>
            <SectionLabel required>제목</SectionLabel>
            <TextField
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              placeholder={isMemo ? '예: B3 배선 확인 요청' : '예: 3월 정기 점검'}
              inputProps={{ 'aria-label': '제목' }}
              autoFocus={!isSelectMode && !isMobile}
            />
          </Box>

          {/* 4. 내용 — 제목 바로 아래. 메타(유형·기간·담당자)를 사이에
              끼우면 쓰려던 내용이 화면 밖으로 밀린다. */}
          <Box>
            <SectionLabel>내용</SectionLabel>
            <RichTextEditor
              value={form.description}
              onChange={(md) => setForm((f) => ({ ...f, description: md }))}
              placeholder={`${noun} 내용을 작성하세요. @ 로 담당자를 멘션하면 알림이 갑니다. 사진은 툴바·붙여넣기·드래그로 첨부됩니다.`}
              minHeight={isMobile ? 160 : 200}
              onUploadImage={canUploadImage ? uploadImage : undefined}
              mentionUsers={mentionUsers}
            />
          </Box>

          {/* 5. 유형 — 일정에만. 칩으로 노출해 한 번의 탭으로 고르게 한다. */}
          {!isMemo && (
            <Box>
              <SectionLabel required>유형</SectionLabel>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }}>
                {SCHEDULE_ONLY_EVENT_TYPES.map((t) => (
                  <Chip
                    key={t}
                    label={EVENT_TYPE_LABEL[t]}
                    onClick={() => setForm((f) => ({ ...f, event_type: t }))}
                    color={form.event_type === t ? 'primary' : 'default'}
                    variant={form.event_type === t ? 'filled' : 'outlined'}
                    // 손가락으로 정확히 누를 수 있는 크기 (모바일 최소 타깃).
                    sx={{ height: 34, px: 0.5, fontWeight: form.event_type === t ? 700 : 400 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* 6. 날짜 */}
          <Box>
            <SectionLabel required={!isMemo}>{isMemo ? '날짜 (선택)' : '기간'}</SectionLabel>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="시작"
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => {
                  // 직접 고친 날짜는 실제 의도다 — 메모 전환 시 유지해야 한다.
                  dateAutoSeededRef.current = false;
                  setForm((f) => ({ ...f, scheduled_at: e.target.value }));
                }}
                InputLabelProps={{ shrink: true }}
                // 아이콘뿐 아니라 입력창 빈 공간을 눌러도 네이티브 피커가 뜨게 한다.
                // 모바일에서 작은 아이콘을 정확히 겨냥하기 어렵다. 미지원은 무시.
                inputProps={{
                  onClick: (e) => (e.currentTarget as HTMLInputElement).showPicker?.(),
                }}
                sx={{ flex: 1, '& input': { colorScheme: isDark ? 'dark' : 'light' } }}
              />
              <TextField
                label="종료"
                type="datetime-local"
                value={form.scheduled_end_at}
                onChange={(e) => setForm((f) => ({ ...f, scheduled_end_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                // 시작이 없으면 기간이 성립하지 않는다.
                disabled={!form.scheduled_at}
                inputProps={{
                  onClick: (e) => (e.currentTarget as HTMLInputElement).showPicker?.(),
                }}
                sx={{ flex: 1, '& input': { colorScheme: isDark ? 'dark' : 'light' } }}
              />
            </Stack>
            {isMemo && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  비워 두면 캘린더에 뜨지 않고 공지사항 목록에만 남습니다.
                </Typography>
                {form.scheduled_at && (
                  <Button
                    size="small"
                    onClick={() =>
                      setForm((f) => ({ ...f, scheduled_at: '', scheduled_end_at: '' }))
                    }
                  >
                    날짜 지우기
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          {/* 7. 담당자 — 지정 시 메일이 가므로 저장 직전 마지막으로 확인. */}
          <Box>
            <SectionLabel>담당자</SectionLabel>
            <Autocomplete
              multiple
              freeSolo
              options={userNames}
              value={form.assignees}
              onChange={(_, v) =>
                setForm((f) => ({
                  ...f,
                  assignees: (v as string[]).map((s) => s.trim()).filter(Boolean),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={form.assignees.length === 0 ? '가입자 선택 또는 직접 입력' : ''}
                  helperText="여러 명 지정 가능. 지정하면 등록 시 메일이 갑니다."
                />
              )}
            />
          </Box>

          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={stickyActionsSx}>
        <DraftSavedHint savedAt={savedAt} />
        <Button onClick={onClose} disabled={saving}>
          취소
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 96 }}>
          {saving ? '저장 중...' : editing ? '저장' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleFormDialog;
